import React, { useState } from "react";
import {
  List,
  ListItem,
  ListItemButton,
  Paper,
  Tooltip,
  IconButton,
  Avatar,
  Box,
  Typography,
  Chip,
} from "@mui/material";
import { PictureAsPdf, Edit, Send, EventBusy, PostAdd } from "@mui/icons-material";
import { TherapyChip } from "../../../../atoms/Chip";
import Controls from "../../../../atoms/Controls";
import { mobiliTheme } from "../../../../themes/mobiliTheme";
import { getStatusColor } from "../../../PatientOverview/RxInfo/RxForms/statusColor";
import {
  DoctorReportDto,
  DoctorReportStatusDtoStatusEnum,
  RxDtoProcessingStatusEnum,
} from "../../../../api-client";
import { useApi } from "../../../../api/ApiProvider";
import { update } from "lodash";
import moment from "moment";
import { MobiliAvatar } from "../../../../atoms/MobiliAvatar";
import { formatDateDisplay } from "../../../../helpers/dateFormat";
import notificationStore from "../../../../stores/Notification/notificationStore";
import { formatDispatchInfo } from "../dispatchInfo";
import { therapyRxTermLabel } from "../../../../helpers/therapyRxLabel";
import { termColorKey } from "../../../../helpers/slotTherapy";

/** Maps legacy status values to new display values */
const mapDoctorReportStatus = (status: string | undefined | null): string => {
  if (!status) return DoctorReportStatusDtoStatusEnum.Entwurf;
  const mapping: Record<string, string> = {
    "None - Requested": DoctorReportStatusDtoStatusEnum.Entwurf,
    "Requested": DoctorReportStatusDtoStatusEnum.Entwurf,
    "Incomplete": DoctorReportStatusDtoStatusEnum.Entwurf,
    "Complete": DoctorReportStatusDtoStatusEnum.Abgegeben,
    "Sent": DoctorReportStatusDtoStatusEnum.Versendet,
    "Abgabefertig": DoctorReportStatusDtoStatusEnum.Abgegeben,
    "Abgeschlossen": DoctorReportStatusDtoStatusEnum.Versendet,
  };
  return mapping[status] ?? status;
};

/** Returns a color for a given mapped status */
const getReportStatusColor = (status: string | undefined | null): string => {
  const mapped = mapDoctorReportStatus(status);
  switch (mapped) {
    case DoctorReportStatusDtoStatusEnum.Entwurf:
      return "#9e9e9e"; // grey
    case DoctorReportStatusDtoStatusEnum.Abgegeben:
      return "#f57c00"; // orange
    case DoctorReportStatusDtoStatusEnum.Versendet:
      return "#388e3c"; // green
    default:
      return "#9e9e9e";
  }
};

interface DoctorReportSelectorProps {
  doctorReports: DoctorReportDto[];
  selectedRow: any;
  handleRowClick: (report: DoctorReportDto) => void;
  handleEditClick: (report: DoctorReportDto) => void;
  handleOpenClick: (report: string) => void;
  handleSendClick: (report: DoctorReportDto) => void;
  actionLoading: boolean;
}

const DoctorReportSelector: React.FC<DoctorReportSelectorProps> = ({
  doctorReports,
  selectedRow,
  handleRowClick,
  handleOpenClick,
  handleSendClick,
  handleEditClick,
  actionLoading,
}) => {
  const { clinicalDocumentation } = useApi();

  // Local state to manage statuses
  const [statuses, setStatuses] = useState<Record<string, DoctorReportStatusDtoStatusEnum | null>>(
    () =>
      doctorReports.reduce(
        (acc, report: any) => {
          acc[report.id] = report.status || null;
          return acc;
        },
        {} as Record<string, DoctorReportStatusDtoStatusEnum | null>,
      ),
  );

  const handleCheckboxChange = async (
    report: DoctorReportDto,
    newStatus: DoctorReportStatusDtoStatusEnum,
  ) => {
    const updatedStatus = statuses[report.id] === newStatus ? null : newStatus;

    // Update the local state
    setStatuses((prevStatuses) => ({
      ...prevStatuses,
      [report.id]: updatedStatus,
    }));

    // Update the backend
    try {
      await clinicalDocumentation.updateClinicalDoctorReportStatus(report.id, {
        ...report,
        status: updatedStatus as any,
      });
      notificationStore.showMessage("Status des Therapieberichts erfolgreich aktualisiert!", "success");
    } catch (error: any) {
      notificationStore.showMessage(
        "Der Status des Therapieberichts konnte nicht aktualisiert werden!",
        "error",
      );
    }
  };

  return (
    <List component="nav" aria-label="therapy list" sx={{ padding: 0 }}>
      {doctorReports.map((report: any, index) => {
        const isSelected = selectedRow.id === report.id;
        const displayStatus = mapDoctorReportStatus(report.status);
        const statusColor = getReportStatusColor(report.status);
        const dispatchDescription = formatDispatchInfo(report);
        // A report is ready to send only once a current (non-stale) PDF has been
        // generated and stored. Editing the report marks the PDF stale again.
        const pdfReady = !!report.documentId && !report.isPdfStale;
        // Closed before report PDFs were linked: the Therapiebericht exists only in
        // the e-Akte, so there is nothing here to (re)generate.
        const pdfOnlyInEAkte =
          displayStatus === DoctorReportStatusDtoStatusEnum.Versendet &&
          !report.isPdfStale &&
          !report.documentId;

        return (
          <React.Fragment key={report.id || index}>
            <ListItem sx={{ padding: "4px 8px" }}>
              <ListItemButton
                onClick={() => handleRowClick(report)}
                selected={isSelected}
                sx={{ padding: "0px", borderRadius: "10px", width: "100%" }}
              >
                <Paper
                  elevation={isSelected ? 3 : 1}
                  sx={{
                    padding: "10px 12px",
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    backgroundColor: isSelected
                      ? "secondary.light"
                      : mobiliTheme.lowLevelContainerBackground,
                    gap: "6px",
                    borderRadius: "10px",
                    borderLeft: `4px solid ${statusColor}`,
                    transition: "box-shadow 0.2s ease-in-out, transform 0.15s ease-in-out",
                    "&:hover": {
                      boxShadow: 3,
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  {/* Row 1: Status, Avatar, Action Icons */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: "6px", width: "100%" }}>
                    {/* Status Chip */}
                    <Chip
                      label={displayStatus || "–"}
                      size="small"
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        minWidth: "80px",
                        height: "24px",
                        backgroundColor: "#fff",
                        color: statusColor,
                        borderRadius: "6px",
                        flexShrink: 0,
                      }}
                    />

                    {report.isPdfStale && (
                      <Chip
                        label="PDF veraltet"
                        size="small"
                        color="warning"
                        sx={{
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          height: "24px",
                          borderRadius: "6px",
                          flexShrink: 0,
                        }}
                      />
                    )}

                    {pdfOnlyInEAkte && (
                      <Chip
                        label="PDF in e-Akte"
                        size="small"
                        color="info"
                        sx={{
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          height: "24px",
                          borderRadius: "6px",
                          flexShrink: 0,
                        }}
                      />
                    )}

                    {/* Avatar */}
                    <MobiliAvatar
                      user={report.assignedToUser || report.createdByUser}
                      sx={{ width: 28, height: 28, flexShrink: 0 }}
                    />

                    {/* Action Icons - right aligned */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: "2px", flexShrink: 0 }}>
                      <Tooltip
                        title={
                          pdfOnlyInEAkte
                            ? "Dieser Therapiebericht wurde vor der PDF-Verknüpfung versendet. Das PDF finden Sie im Tab „e-Akte“."
                            : pdfReady
                              ? "Therapiebericht anzeigen"
                              : "PDF erzeugen"
                        }
                        arrow
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleOpenClick(report.id);
                            }}
                            disabled={actionLoading || pdfOnlyInEAkte}
                            color={report.isPdfStale ? "warning" : "default"}
                            sx={{ "&:hover": { color: "primary.main" } }}
                          >
                            <PictureAsPdf sx={{ fontSize: "1.1rem" }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Bericht erstellen" arrow>
                        <IconButton
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleEditClick(report);
                          }}
                          sx={{ "&:hover": { color: "success.main" } }}
                        >
                          <PostAdd sx={{ fontSize: "1.1rem" }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Bearbeiten" arrow>
                        <IconButton
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleEditClick(report);
                          }}
                          sx={{ "&:hover": { color: "warning.main" } }}
                        >
                          <Edit sx={{ fontSize: "1.1rem" }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={
                          pdfReady
                            ? "Per E-Mail senden"
                            : pdfOnlyInEAkte
                              ? "Bereits versendet — das PDF finden Sie im Tab „e-Akte“."
                              : "Bitte zuerst das PDF erzeugen"
                        }
                        arrow
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleSendClick(report);
                            }}
                            disabled={actionLoading || !pdfReady}
                            sx={{ "&:hover": { color: "info.main" } }}
                          >
                            <Send sx={{ fontSize: "1.1rem" }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>

                  {/* Row 2: Rx Number, Date, Therapy Chips */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap", pl: "4px" }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, color: "text.primary", fontSize: "0.85rem", whiteSpace: "nowrap" }}
                    >
                      {report.rx?.rxNumber}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.disabled" }}>•</Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", fontSize: "0.8rem", whiteSpace: "nowrap" }}
                    >
                      {report.createdAt ? moment(report.createdAt).format("DD.MM.YYYY") : "–"}
                    </Typography>
                    {report.rx?.therapyRxs && report.rx.therapyRxs.length > 0 && (
                      <>
                        <Typography variant="caption" sx={{ color: "text.disabled" }}>•</Typography>
                        {report.rx.therapyRxs
                          .slice()
                          .sort((a: any, b: any) => a.order - b.order)
                          .map((therapyRx: any, idx: number) => (
                            <TherapyChip
                              key={idx}
                              therapyName={therapyRxTermLabel(therapyRx)}
                              colorKey={
                                therapyRx.prescribedTherapy?.id ?? therapyRx.prescribedTherapyId
                                  ? termColorKey(
                                      therapyRx.prescribedTherapy?.id ?? therapyRx.prescribedTherapyId
                                    )
                                  : undefined
                              }
                              amount={therapyRx.amount}
                            />
                          ))}
                      </>
                    )}
                  </Box>
                  {dispatchDescription && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap", pl: "4px" }}>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", fontWeight: 600, fontSize: "0.75rem" }}
                      >
                        Versendet:
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", fontStyle: "italic", fontSize: "0.75rem" }}
                      >
                        {dispatchDescription}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </ListItemButton>
            </ListItem>
          </React.Fragment>
        );
      })}
    </List>
  );
};

export default DoctorReportSelector;
